import { CONNECT_BRAND } from '@/lib/connect/brand'
import type { ProductBrand } from '@/lib/product-hosts'
/**
 * The AI hosts Connect is reached from, and how each one is pointed at it.
 *
 * Connect is host-neutral by design: the entry condition is "can this host
 * speak remote MCP", not "is it on our list". So this table is a set of
 * WORKED EXAMPLES, not an allowlist — the last entry is deliberately the
 * generic one, and nothing in the product consults this file to decide whether
 * a host may connect.
 *
 * Three things are kept honest here:
 *
 * The endpoint is never hardcoded. It is threaded in from
 * `GET /api/connect/console/overview`, which echoes the deployment's
 * `CONNECT_MCP_RESOURCE`. That value is baked into signed metadata and into
 * every token's audience, so a console printing a guessed URL would be telling
 * people to connect to a resource that will refuse them.
 *
 * Menu names are described, not asserted. Hosts rename "connectors" /
 * "integrations" / "apps" / "tools" between releases, and a screenshot-accurate
 * instruction that has since gone stale is worse than one that says what to
 * look for. Every entry therefore carries `verifiedAt` + `sourceUrl`, and the
 * guide renders them: a reader can tell a checked instruction from a guess.
 *
 * Config shapes are per host, not one generic block. An earlier version of this
 * file asserted that "Claude Desktop, Cursor, VS Code, Windsurf and Goose all
 * read the same shape" and served one `mcpServers` snippet to all of them. VS
 * Code does not: its key is `servers`. Pasting the wrong wrapper registers
 * nothing and reports no error, which is the worst failure a copy-paste
 * instruction can have.
 */

export type ConnectHostKind = 'chat' | 'terminal' | 'desktop' | 'custom'

/** One "this is what bites people" note, shown in the guide's pitfalls list. */
export interface ConnectHostPitfall {
  title: string
  body: string
}

/** A place a host keeps its MCP config, and what putting it there means. */
export interface ConnectHostConfigLocation {
  label: string
  path: string
}

/** An install scope a host offers, with the flag that selects it. */
export interface ConnectHostScope {
  flag: string
  meaning: string
}

export interface ConnectHost {
  id: string
  name: string
  kind: ConnectHostKind
  /** Where the result of a task appears, in the PRD's terms. */
  resultLocation: string
  /** How the host is pointed at Connect. `null` when there is no snippet to copy. */
  snippet: ((endpoint: string) => string) | null
  /** Language tag for the snippet block, when there is one. */
  snippetLang?: 'bash' | 'json' | 'toml'
  /** Prose steps, for hosts configured through their own UI. */
  steps: string[]

  /**
   * What must already be true before the steps can work at all.
   *
   * Only present where the host really imposes one. Both chat hosts do, and
   * neither prerequisite was mentioned before this guide existed: ChatGPT needs
   * developer mode, which a workspace admin enables; Claude limits Free
   * accounts to a single custom connector and lets only Owners add one on Team
   * and Enterprise. Someone who cannot satisfy these will otherwise read three
   * cheerful steps and conclude Connect is broken.
   */
  prerequisites?: string[]
  /** Where this host keeps the config, when that is a file the reader edits. */
  configLocations?: ConnectHostConfigLocation[]
  /** Install scopes this host offers, when it offers more than one. */
  scopes?: ConnectHostScope[]
  /** How authorization is actually triggered — separate from installing. */
  authorize: string[]
  /** One sentence a reader can paste into the host to prove it works. */
  verifyPrompt: string
  /** What a working answer looks like, so a plausible-but-wrong one is visible. */
  verifyExpect: string
  pitfalls: ConnectHostPitfall[]
  /**
   * Removing it — always two things, and readers reliably do only the first.
   * Deleting local config stops this machine from connecting; it does not end
   * the grant. Revoking ends the grant and its whole refresh family.
   */
  remove: string[]
  /** `YYYY-MM-DD` this entry was last checked against `sourceUrl`. */
  verifiedAt: string
  /** The official document this entry was written from. */
  sourceUrl: string
  /** Why this entry is less precise than the others, when it is. */
  verifiedNote?: string
}

/**
 * The de-facto cross-client wrapper: a `mcpServers` object keyed by server
 * name. Claude Desktop, Windsurf and Goose read this shape, and Claude Code
 * accepts it through `claude mcp add-json`.
 *
 * NOT used for VS Code (key is `servers`) and NOT used for Cursor (whose
 * documented remote entry carries no `type`). Those have their own builders
 * below rather than one snippet bent to fit three specs.
 */
function mcpServersJson(endpoint: string, serverName: string): string {
  return JSON.stringify(
    { mcpServers: { [serverName]: { type: 'http', url: endpoint } } },
    null,
    2,
  )
}

/**
 * Cursor's documented remote entry: `url` only.
 *
 * Cursor's docs show `type` for neither remote nor OAuth examples, and its
 * OAuth example takes a client id and secret — which Connect does not issue.
 * Every Connect client is a public client authenticated by PKCE alone
 * (contracts.md §7.2), so the entry stays minimal and the 401 pointer does the
 * rest.
 */
function cursorMcpJson(endpoint: string, serverName: string): string {
  return JSON.stringify({ mcpServers: { [serverName]: { url: endpoint } } }, null, 2)
}

/** VS Code's `mcp.json`: the wrapper key is `servers`, not `mcpServers`. */
function vscodeMcpJson(endpoint: string, serverName: string): string {
  return JSON.stringify(
    { servers: { [serverName]: { type: 'http', url: endpoint } } },
    null,
    2,
  )
}

/**
 * What to ask first, in every host.
 *
 * Deliberately a discovery call rather than a data read: `search_capabilities`
 * is resident for every grant (mcp_server.py), so it proves the token and the
 * catalog in one turn without requiring the reader to have connected an account
 * first. A verification step that fails for
 * someone with no GA4 connection would be testing the wrong thing.
 */
const VERIFY_PROMPT = `Using ${CONNECT_BRAND.name}, search for what capabilities I can reach.`

const VERIFY_EXPECT =
  'A list of capabilities with names, what each reads and what each costs. An empty list means the search matched nothing — try broader words. An error naming authorization means the host never completed the browser step.'

/** The local config name follows the web product, never the MCP endpoint. */
export function connectMcpServerName(product: ProductBrand['product']): string {
  return product
}

/** Bind all setup and removal instructions to the same product identity. */
export function connectHostsForProduct(
  product: ProductBrand['product'],
): readonly ConnectHost[] {
  const serverName = connectMcpServerName(product)
  return [
    {
      id: 'claude-code',
      name: 'Claude Code',
      kind: 'terminal',
      resultLocation: 'The current task or session',
      snippet: endpoint => `claude mcp add --transport http ${serverName} ${endpoint}`,
      snippetLang: 'bash',
      steps: [
        'Run the command in any project. It writes the configuration and prints an `Added …` line.',
        'Authorize separately — adding a server does not open a browser.',
        'Run `claude mcp list` to confirm the server reads `✔ Connected`.',
      ],
      scopes: [
        {
          flag: '(default)',
          meaning: 'Local — this project, your machine only.',
        },
        {
          flag: '--scope user',
          meaning: 'Every project on this machine.',
        },
        {
          flag: '--scope project',
          meaning:
            `Written to \`.mcp.json\` at the project root and committed, so teammates are prompted to approve it. Each teammate still authorizes their own ${CONNECT_BRAND.name} grant.`,
        },
      ],
      authorize: [
        `Inside Claude Code, run \`/mcp\` and follow the browser flow — or, from your shell, \`claude mcp login ${serverName}\`.`,
        `The ${CONNECT_BRAND.name} consent page names the host, the workspace the grant will read through, and what the connection will be able to do.`,
        'Approving creates the grant listed above on this page. Cancelling creates nothing.',
        `On a machine with no browser (SSH, headless Linux), \`claude mcp login ${serverName} --no-browser\` prints the URL and takes the pasted callback.`,
      ],
      verifyPrompt: VERIFY_PROMPT,
      verifyExpect: VERIFY_EXPECT,
      pitfalls: [
        {
          title: '`claude mcp add` does not sign you in',
          body: `It only writes configuration. Until you run \`/mcp\` or \`claude mcp login ${serverName}\`, the server sits at \`! Needs authentication\` and every tool call fails.`,
        },
        {
          title: 'Non-interactive runs cannot authorize',
          body: '`claude -p` and Agent SDK runs have no `/mcp` panel, so they report the tools as unavailable rather than opening a browser. Sign in once from an interactive session first.',
        },
        {
          title: 'Re-adding the same name fails',
          body: `Running \`claude mcp add\` again with \`${serverName}\` at the same scope errors with \`already exists\`. Remove it first, or add it at a different scope.`,
        },
        {
          title: '`✘ Failed to connect` is the server, not the command',
          body: `\`claude mcp get ${serverName}\` prints an \`Issue:\` line with the status code. A 401 there means the grant was revoked — re-authorize.`,
        },
      ],
      remove: [
        `\`claude mcp remove ${serverName}\` deletes the configuration on this machine.`,
        `\`claude mcp logout ${serverName}\` clears the stored tokens — or use "Clear authentication" in the \`/mcp\` menu.`,
        'Then revoke the grant above. Deleting local config does not end the grant; only Revoke does, and it ends the refresh family with it.',
      ],
      verifiedAt: '2026-08-25',
      sourceUrl: 'https://code.claude.com/docs/en/mcp',
    },
    {
      id: 'codex',
      name: 'Codex',
      kind: 'terminal',
      resultLocation: 'The current task or session',
      snippet: endpoint =>
        [
          `[mcp_servers.${serverName}]`,
          `url = "${endpoint}"`,
          'auth = "oauth"',
        ].join('\n'),
      snippetLang: 'toml',
      steps: [
        'Add the block to your Codex config file.',
        `Run \`codex mcp login ${serverName}\` and complete the browser flow.`,
        'In the Codex app or IDE extension you can instead use its MCP settings: add a server, choose Streamable HTTP, and paste the endpoint.',
      ],
      configLocations: [
        { label: 'User', path: '~/.codex/config.toml' },
        {
          label: 'Project (trusted projects only)',
          path: '.codex/config.toml',
        },
      ],
      authorize: [
        `\`codex mcp login ${serverName}\` opens the ${CONNECT_BRAND.name} consent page.`,
        'Codex registers itself dynamically — there is no client id to obtain or paste.',
        'Approving creates the grant listed above on this page.',
      ],
      verifyPrompt: VERIFY_PROMPT,
      verifyExpect: VERIFY_EXPECT,
      pitfalls: [
        {
          title: 'ChatGPT on the web does not read this file',
          body: `The config above reaches local Codex clients only — CLI, IDE extension, desktop app. Connecting ${CONNECT_BRAND.name} to ChatGPT itself is a separate path; see the ChatGPT guide.`,
        },
        {
          title: 'Do not set a bearer token',
          body: `\`bearer_token_env_var\` and static \`http_headers\` are for servers that take a fixed key. ${CONNECT_BRAND.name} uses OAuth; a stray Authorization header makes the host report a connection failure instead of falling back to the browser flow.`,
        },
      ],
      remove: [
        `Delete the \`[mcp_servers.${serverName}]\` block from the config file.`,
        'Then revoke the grant above — removing the block stops this machine, not the grant.',
      ],
      verifiedAt: '2026-08-25',
      sourceUrl: 'https://learn.chatgpt.com/docs/extend/mcp?surface=cli',
    },
    {
      id: 'cursor',
      name: 'Cursor',
      kind: 'terminal',
      resultLocation: 'The current task or session',
      snippet: endpoint => cursorMcpJson(endpoint, serverName),
      snippetLang: 'json',
      steps: [
        'Add the server under Cursor’s MCP settings, or paste the block into the config file.',
        `Cursor discovers the authorization server from ${CONNECT_BRAND.name}’s 401 and opens the browser on first use.`,
        'If it does not prompt, look for a sign-in or "needs authentication" control on the server in Cursor’s MCP panel.',
      ],
      configLocations: [
        { label: 'Global', path: '~/.cursor/mcp.json' },
        { label: 'Project', path: '.cursor/mcp.json' },
      ],
      authorize: [
        `The ${CONNECT_BRAND.name} consent page opens on first use and names the host, the workspace and what the connection covers.`,
        'Approving creates the grant listed above on this page.',
      ],
      verifyPrompt: VERIFY_PROMPT,
      verifyExpect: VERIFY_EXPECT,
      pitfalls: [
        {
          title: 'Leave the `auth` block out',
          body: `Cursor’s docs show an \`auth\` object taking a client id and secret. ${CONNECT_BRAND.name} issues neither — every client is public and bound by PKCE — so filling it in is not "more secure", it configures credentials that do not exist.`,
        },
        {
          title: 'One entry per machine, one grant per entry',
          body: 'A laptop and a desktop each register separately, so each appears as its own host above. Revoking one leaves the other working, which is the point.',
        },
      ],
      remove: [
        `Delete the \`${serverName}\` entry from \`mcp.json\`.`,
        'Then revoke the grant above.',
      ],
      verifiedAt: '2026-08-25',
      sourceUrl: 'https://cursor.com/docs/context/mcp',
    },
    {
      id: 'vscode',
      name: 'VS Code',
      kind: 'terminal',
      resultLocation: 'The current task or session',
      snippet: endpoint => vscodeMcpJson(endpoint, serverName),
      snippetLang: 'json',
      steps: [
        'Create or open `.vscode/mcp.json` in your project, or run the **MCP: Open User Configuration** command to edit the copy in your user profile.',
        'Paste the block. Note the wrapper key is `servers` — VS Code does not read `mcpServers`.',
        'Complete the browser authorization when VS Code prompts.',
      ],
      configLocations: [
        { label: 'Workspace', path: '.vscode/mcp.json' },
        {
          label: 'User profile',
          path: 'MCP: Open User Configuration',
        },
      ],
      authorize: [
        `The ${CONNECT_BRAND.name} consent page opens from VS Code and names the host, the workspace and what the connection covers.`,
        'Approving creates the grant listed above on this page.',
      ],
      verifyPrompt: VERIFY_PROMPT,
      verifyExpect: VERIFY_EXPECT,
      pitfalls: [
        {
          title: 'The wrapper key is `servers`',
          body: 'Blocks copied from another client’s documentation use `mcpServers`. VS Code ignores that key silently — no server appears and no error is shown. This is the single most common way a correct endpoint still ends up unreachable.',
        },
        {
          title: 'Do not hardcode a token',
          body: `VS Code’s docs warn against putting secrets in this file. With ${CONNECT_BRAND.name} there is nothing to put: authorization is a browser flow, not a key.`,
        },
      ],
      remove: [
        `Delete the \`${serverName}\` entry from \`mcp.json\`.`,
        'Then revoke the grant above.',
      ],
      verifiedAt: '2026-08-25',
      sourceUrl: 'https://code.visualstudio.com/docs/copilot/chat/mcp-servers',
    },
    {
      id: 'chatgpt',
      name: 'ChatGPT',
      kind: 'chat',
      resultLocation: 'The current conversation thread',
      snippet: null,
      prerequisites: [
        'Developer mode must be on for your account.',
        'On a workspace, an admin enables it first — the permission lives in the workspace’s settings, under the controls for connected data and custom MCP connectors. Without it the option to add one does not appear.',
      ],
      steps: [
        'Turn on developer mode in ChatGPT’s settings.',
        'Open the screen where ChatGPT manages connectors, apps or plugins, and add a remote MCP server.',
        'Paste the endpoint above, including its path — not just the hostname.',
        'Complete the browser authorization, then return to your conversation.',
      ],
      authorize: [
        `ChatGPT sends you through the ${CONNECT_BRAND.name} consent page, which names the host, the workspace and what the connection covers.`,
        'Approving creates the grant listed above on this page.',
      ],
      verifyPrompt: VERIFY_PROMPT,
      verifyExpect: VERIFY_EXPECT,
      pitfalls: [
        {
          title: 'A local Codex config does not carry over',
          body: `ChatGPT on the web does not read \`~/.codex/config.toml\`. Adding ${CONNECT_BRAND.name} to Codex and adding it to ChatGPT are two separate setups producing two separate grants.`,
        },
        {
          title: 'The option is missing until an admin turns it on',
          body: 'On a workspace this is an administrator permission, not something to retry. If you cannot find where to add a custom server, that is the likely reason.',
        },
      ],
      remove: [
        'Remove the connector in ChatGPT.',
        'Then revoke the grant above.',
      ],
      verifiedAt: '2026-08-25',
      sourceUrl:
        'https://help.openai.com/en/articles/12584461-developer-mode-and-mcp-apps-in-chatgpt',
      verifiedNote:
        'Menu wording here is described rather than quoted: OpenAI’s help article is not publicly retrievable, so this entry names what to look for and links the official page instead of asserting an exact click path.',
    },
    {
      id: 'claude-web',
      name: 'Claude (web & desktop)',
      kind: 'chat',
      resultLocation: 'The current conversation thread',
      snippet: null,
      prerequisites: [
        'Custom connectors are available on Free, Pro, Max, Team and Enterprise. A Free account may hold one custom connector in total.',
        'On Team and Enterprise, an Owner adds the connector for the organization first, under Organization settings → Connectors. Members then connect to it individually.',
        `${CONNECT_BRAND.name} must be reachable from Anthropic’s cloud over the public internet — which the hosted endpoint is.`,
      ],
      steps: [
        'Open Customize → Connectors and choose "Add custom connector".',
        'Paste the endpoint above as the remote MCP server URL.',
        'Leave the OAuth client id and secret under Advanced settings empty.',
        'Complete the browser authorization, then return to your conversation.',
      ],
      authorize: [
        `Claude sends you through the ${CONNECT_BRAND.name} consent page, which names the host, the workspace and what the connection covers.`,
        'Approving creates the grant listed above on this page.',
      ],
      verifyPrompt: VERIFY_PROMPT,
      verifyExpect: VERIFY_EXPECT,
      pitfalls: [
        {
          title: 'Leave the advanced OAuth fields blank',
          body: `They are optional, and ${CONNECT_BRAND.name} issues no client id or secret — clients register themselves dynamically. Inventing values there produces a connector that cannot authorize.`,
        },
        {
          title: 'Research can call tools without asking each time',
          body: `Claude’s research features may invoke connector tools without per-action approval. A change to one of your accounts still waits for you — ${CONNECT_BRAND.name} only reaches a write that requires review, and you approve it on the link the agent gives you. What can happen unasked is spending: paid ${CONNECT_BRAND.name}-supplied capabilities cost credits per call, so set a per-call cap on this host above if that matters.`,
        },
        {
          title: 'On a team, one Owner goes first',
          body: 'If "Add custom connector" is absent, an Owner has not added it for the organization yet. Members connect to an existing connector; they do not create one.',
        },
      ],
      remove: [
        'Remove the connector under Customize → Connectors.',
        'Then revoke the grant above.',
      ],
      verifiedAt: '2026-08-25',
      sourceUrl:
        'https://support.claude.com/en/articles/11175166-about-custom-connectors-remote-mcp',
    },
    {
      id: 'custom',
      name: 'Your own agent',
      kind: 'custom',
      resultLocation: 'Wherever your agent writes its output',
      snippet: endpoint => mcpServersJson(endpoint, serverName),
      snippetLang: 'json',
      steps: [
        'Point any MCP client at the endpoint above — no pre-registration needed.',
        'The 401 carries an RFC 9728 pointer, so your client discovers the authorization server on its own and registers itself.',
        'A person approves the connection once, in the browser; the grant is revocable here at any time.',
      ],
      authorize: [
        'Your client registers dynamically (RFC 7591) and runs authorization code with PKCE S256 — no client secret is issued or accepted.',
        'Redirect URIs must be https or loopback http. Custom schemes, fragments and host-less URIs are rejected at registration.',
        `A \`scope\` parameter is accepted and ignored — a grant covers the catalog ${CONNECT_BRAND.name} publishes. The consent page is where authorization actually happens.`,
      ],
      verifyPrompt: VERIFY_PROMPT,
      verifyExpect: VERIFY_EXPECT,
      pitfalls: [
        {
          title: 'A client that ignores the 401 pointer cannot start',
          body: 'Discovery is the only way to find the authorization server. Clients that speak stdio MCP only can bridge with `mcp-remote`, which performs the flow and caches the tokens.',
        },
        {
          title: 'Closing the tab is not a denial',
          body: 'Cancel on the consent page sends your client an `access_denied` it can act on. Closing the window sends nothing, and a well-behaved client waits.',
        },
      ],
      remove: [
        'Delete the endpoint from your client’s configuration.',
        'Then revoke the grant above — that ends the access token, the refresh token and its whole rotation family at once.',
      ],
      verifiedAt: '2026-08-25',
      sourceUrl: 'https://modelcontextprotocol.io/specification',
    },
  ]
}

/** Legacy callers retain the Soku config name. */
export const CONNECT_HOSTS = connectHostsForProduct('soku')

/** Plain-language label for a supply type, in the words the PRD uses. */
export const ACCESS_MODE_LABEL: Record<string, string> = {
  customer_connection: 'Your account · free',
  soku_data: `${CONNECT_BRAND.name} data · credits`,
  hybrid: 'Both',
}

/**
 * One sentence on what a supply type means for the reader's wallet and data.
 *
 * The label leads with the cost ("Your account · free") because that is what a
 * reader is actually deciding between; the blurb behind it still names WHOSE
 * credential runs the call, which is the fact the two must never merge on.
 *
 * Kept beside the label because the distinction is the product's central claim:
 * "Soku is connected to this provider" must never be readable as "I authorized
 * my own account".
 */
export const ACCESS_MODE_BLURB: Record<string, string> = {
  customer_connection:
    `Reads an account you connected yourself. ${CONNECT_BRAND.name} pays nobody for it, so it costs 0 credits.`,
  soku_data:
    `Runs on data ${CONNECT_BRAND.name} buys for you. Your AI is quoted a price before it runs, then charged what it really cost.`,
  hybrid:
    'Uses both in one answer. The free part and the paid part are quoted and recorded separately.',
}

/**
 * What every host is charged the same way for, rendered once in the guide
 * rather than repeated per host.
 *
 * These are product rules, not host behaviour: putting them in each entry would
 * be seven copies of one promise, and the day one copy drifts is the day the
 * promise stops being one.
 */
export function connectCostRules(
  product: ProductBrand['product'],
): readonly string[] {
  return [
    `Reading an account you connected costs 0 credits — ${CONNECT_BRAND.name} pays no vendor for it.`,
    `A ${CONNECT_BRAND.name}-supplied capability is quoted to your agent before it runs, then charged at what the real response cost.`,
    'A failed call is never charged. Quoting, admission, budget and execution all happen before settlement, so there is no window where you have paid for nothing.',
    'A successful call that returns no rows is charged. The query ran, and "there is nothing in that window" is the answer.',
    // toolfare is pay-as-you-go: admission has no unpaid-trial limits there
    // (`connect_admission.py` skips them for payg), so the Soku rule is wrong.
    product === 'toolfare'
      ? 'No subscription is required. A Built-in call runs when the balance covers its quote.'
      : 'Reading the accounts you connected yourself costs 0 credits on any plan. ' +
        'Without a paid plan, Built-in calls are limited to 50 credits each and 200 ' +
        'in total, with enough balance. Beyond that, an active paid plan is needed.',
    'Per-call, daily and 30-day caps are set per host, above. An empty cap field means no cap for that host.',
  ]
}
